import type { TextAreaRef } from "antd/lib/input/TextArea";
import { useCallback, useEffect, useRef, useState } from "react";

import { IconArrowRight, IconPodcast } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import "utilities/dictation/dictationMic.less";
import { appendDictation, useDictation } from "utilities/dictation/useDictation";

/**
 * The textarea's classes inside {@link AskComposerField}. antd's placeholder
 * grey is too faint to read on white, and the card is the field's frame, so
 * the textarea's own ring, focus edge and padding are flattened.
 */
export const ASK_COMPOSER_TEXTAREA_CLASS =
    "bg-transparent bg-none p-0 text-lg shadow-none placeholder:text-fg-tertiary";

/** What the field reads while the mic is on. */
export const ASK_COMPOSER_LISTENING_PLACEHOLDER = "Listening — tap the mic to stop";

interface IUseAskComposerDictationArgs {
    value: string;
    onChange: (value: string) => void;
    /** No question can be asked: the agent is unavailable. */
    disabled: boolean;
    /** An answer is streaming; a new question waits for it. */
    isBusy: boolean;
    /** Receives the trimmed question, including any words still being dictated. */
    onSubmit: (question: string) => void;
}

/**
 * Dictation into a composer's text, and asking from it. Asking while
 * dictating, or just after stop, finishes dictating first: the last phrase is
 * still being recognized, and asking now would leave it to land in the
 * emptied box.
 */
export function useAskComposerDictation({
    value,
    onChange,
    disabled,
    isBusy,
    onSubmit,
}: IUseAskComposerDictationArgs) {
    // Read through a ref because one recognizer event can deliver several
    // phrases before the parent re-renders with the new value.
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    const dictation = useDictation({
        onPhrase: (phrase) => {
            const next = appendDictation(valueRef.current, phrase);
            valueRef.current = next;
            onChange(next);
        },
    });
    const isRecording = dictation.isListening;
    /** The text with the phrase still being heard; only final phrases reach `value`. */
    const shownValue = isRecording ? appendDictation(value, dictation.interimText) : value;

    const [isAskQueued, setIsAskQueued] = useState(false);
    const canSubmit =
        !disabled &&
        !isBusy &&
        !isAskQueued &&
        (shownValue.trim().length > 0 || dictation.isSettling);

    const submitNow = useCallback(() => {
        // Anything the recognizer still has after the stop deadline belongs to this
        // question, now asked.
        dictation.cancel();
        onSubmit(value.trim());
    }, [dictation, value, onSubmit]);

    const submit = useCallback(() => {
        if (!canSubmit) {
            return;
        }
        if (!isRecording && !dictation.isSettling) {
            submitNow();
            return;
        }
        if (isRecording) {
            dictation.stop();
        }
        setIsAskQueued(true);
    }, [canSubmit, isRecording, dictation, submitNow]);

    // Phrases land through `onChange` before the session's end, so by now `value`
    // holds them all.
    useEffect(() => {
        if (!isAskQueued || dictation.isListening || dictation.isSettling) {
            return;
        }
        setIsAskQueued(false);
        if (!disabled && !isBusy && value.trim().length > 0) {
            submitNow();
        }
    }, [isAskQueued, dictation, disabled, isBusy, value, submitNow]);

    const toggleRecording = useCallback(() => {
        if (isRecording) {
            dictation.stop();
        } else {
            dictation.start();
        }
    }, [isRecording, dictation]);

    return {
        dictation,
        isRecording,
        shownValue,
        isAskQueued,
        canSubmit,
        submit,
        toggleRecording,
    };
}

export type AskComposerDictation = ReturnType<typeof useAskComposerDictation>;

interface IAskComposerActionProps {
    composer: AskComposerDictation;
    /** No question can be asked: the agent is unavailable. */
    disabled: boolean;
    /** Followed by `-dictate` or `-submit`. */
    testIdPrefix: string;
}

/** The dictation mic, for an {@link AskComposerField}'s `actions`. Nothing where dictation is unsupported. */
export function AskComposerMic({ composer, disabled, testIdPrefix }: IAskComposerActionProps) {
    const { dictation, isRecording, isAskQueued, toggleRecording } = composer;
    if (!dictation.isSupported) {
        return null;
    }
    return (
        <BTButton
            type={isRecording ? "danger" : "tertiary"}
            icon={<IconPodcast />}
            aria-label={isRecording ? "Stop dictating" : "Dictate a question"}
            aria-pressed={isRecording}
            className={cn("rounded-full", isRecording && "dictation-mic--live")}
            onClick={toggleRecording}
            disabled={disabled || isAskQueued}
            data-testid={`${testIdPrefix}-dictate`}
        />
    );
}

interface IAskComposerSubmitProps extends IAskComposerActionProps {
    /** An answer is streaming; the arrow spins until it ends. */
    isBusy: boolean;
}

/** The Ask arrow, for an {@link AskComposerField}'s `actions`. */
export function AskComposerSubmit({
    composer,
    disabled,
    isBusy,
    testIdPrefix,
}: IAskComposerSubmitProps) {
    const { isAskQueued, canSubmit, submit } = composer;
    return (
        <BTButton
            type="secondary"
            icon={<IconArrowRight />}
            aria-label="Ask"
            className="rounded-full"
            onClick={submit}
            disabled={disabled || !canSubmit}
            loading={isBusy || isAskQueued}
            data-testid={`${testIdPrefix}-submit`}
        />
    );
}

interface IAskComposerFieldProps {
    /** Focused by a click in the card's empty space. */
    fieldRef: React.RefObject<TextAreaRef>;
    cardRef?: React.RefObject<HTMLDivElement>;
    composer: AskComposerDictation;
    /**
     * The row under the text, right-aligned: each host picks its own buttons,
     * from {@link AskComposerMic}, {@link AskComposerSubmit} or its own.
     */
    actions: React.ReactNode;
    /** Leads the test ids: `-composer`, `-dictation-error`. */
    testIdPrefix: string;
    /**
     * No card of its own: no border, rounding, shadow or background, for a host
     * whose own edges already frame the field (the search dialog). A square
     * background here would paint over the host's rounded corners.
     */
    flush?: boolean;
    /** The label and textarea, styled with {@link ASK_COMPOSER_TEXTAREA_CLASS}. */
    children: React.ReactNode;
}

/**
 * The question box's frame, shared by the Ask page's composer and the search
 * dialog: the card around the textarea, its row of actions, and the dictation
 * error. Which actions, the keys, suggestions and the gray completion stay
 * with each host.
 */
export function AskComposerField({
    fieldRef,
    cardRef,
    composer,
    actions,
    testIdPrefix,
    flush = false,
    children,
}: IAskComposerFieldProps) {
    // Clicks in the card's empty space focus the textarea; antd's autoSize pins
    // its height, so it can't grow to fill the card.
    const focusTextArea = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if ((event.target as HTMLElement).closest("button, textarea") !== null) {
                return;
            }
            fieldRef.current?.focus();
        },
        [fieldRef],
    );

    return (
        <>
            {/* Mouse convenience only: keyboard users reach the textarea
                by tabbing, so the card is not itself focusable. */}
            <div
                ref={cardRef}
                className={cn(
                    "flex cursor-text flex-col gap-8 px-6 pb-4 pt-5",
                    !flush && "rounded-lg border border-default bg-surface-default shadow-raised",
                )}
                onClick={focusTextArea}
                data-testid={`${testIdPrefix}-composer`}
            >
                {children}
                <div className="flex items-center justify-end gap-2">{actions}</div>
            </div>
            {composer.dictation.errorMessage !== null && (
                <BTText
                    size="normal-sm"
                    tag="p"
                    className="m-0 px-2 text-fg-error"
                    testid={`${testIdPrefix}-dictation-error`}
                >
                    {composer.dictation.errorMessage}
                </BTText>
            )}
        </>
    );
}
